import { onboardingService } from "@/services/onboardingServices";
import { FinalData, onboardingState } from "@/types/onboarding";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const initialState: onboardingState = {
  emailVerified: null,
  phoneVerified: null,
  gstVerified: null,
  panVerified: null,
  aadharVerified: null,
  user: null,
  loading: false,
  error: null,
};

// 🔹 Async Thunks
export const verifyEmail = createAsyncThunk(
  "onboarding/verifyEmail",
  async (emailID: string, { rejectWithValue }) => {
    try {
      return await onboardingService.verifyEmail(emailID);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Error verifying email");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const verifyPhone = createAsyncThunk(
  "onboarding/verifyPhone",
  async (phoneID: string, { rejectWithValue }) => {
    try {
      return await onboardingService.verifyPhone(phoneID);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Error verifying phone");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const verifyGST = createAsyncThunk(
  "onboarding/verifyGST",
  async (GST: string, { rejectWithValue }) => {
    try {
      return await onboardingService.verifyGST(GST);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Error verifying GST");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const verifyPAN = createAsyncThunk(
  "onboarding/verifyPAN",
  async (Pan: string, { rejectWithValue }) => {
    try {
      return await onboardingService.verifyPAN(Pan);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Error verifying PAN");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const verifyAadhar = createAsyncThunk(
  "onboarding/verifyAadhar",
  async (aadhar: string, { rejectWithValue }) => {
    try {
      return await onboardingService.verifyAadhar(aadhar);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data || "Error verifying Aadhar"
        );
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const fetchUser = createAsyncThunk(
  "onboarding/fetchUser",
  async (userID: string, { rejectWithValue }) => {
    try {
      return await onboardingService.getUser(userID);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Error fetching user");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const registerUser = createAsyncThunk(
  "onboarding/registerUser",
  async (
    userData: FinalData,
    { rejectWithValue }
  ) => {
    try {
      return await onboardingService.registerUser(userData);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data || "Error registering user"
        );
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

// 🔹 Redux Slice
const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(verifyEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.emailVerified = action.payload.success;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyPhone.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyPhone.fulfilled, (state, action) => {
        state.loading = false;
        state.phoneVerified = action.payload.success;
      })
      .addCase(verifyPhone.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyGST.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyGST.fulfilled, (state, action) => {
        state.loading = false;
        state.gstVerified = action.payload.success;
      })
      .addCase(verifyGST.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyPAN.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyPAN.fulfilled, (state, action) => {
        state.loading = false;
        state.panVerified = action.payload.success;
      })
      .addCase(verifyPAN.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyAadhar.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyAadhar.fulfilled, (state, action) => {
        state.loading = false;
        state.aadharVerified = action.payload.success;
      })
      .addCase(verifyAadhar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default onboardingSlice.reducer;
